import React from 'react';
// import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Divider,
} from '@mui/material';
import psychiatryBackground from '../assets/psychiatry.webp';

const zocdocLink =
  'https://www.zocdoc.com/practice/aspire-bhp-179924?lock=true&isNewPatient=false&referrerType=widget';
 
const conditions = [
  'Depression',
  'Anxiety Disorders',
  'Panic Attacks',
  'Bipolar Disorder',
  'Mood Disorders',
  'Insomnia',
  'ADHD / ADD',
  'PTSD',
  'OCD',
  'Personality Disorders',
  'Schizophrenia',
  'Substance Use Concerns',
  'Other Mental Health Conditions',
];

const Psychiatry = () => (
  <>
    <Helmet>
      <title>Psychiatry & Medication Management | Aspire Behavioral Health</title>
      <meta
        name="description"
        content="Aspire Behavioral Health provides personalized psychiatry and medication management for depression, anxiety, ADHD, mood disorders, insomnia, PTSD, and other mental health concerns."
      />
    </Helmet>

    {/* Hero Section */}
    <Box
      sx={{
        position: 'relative',

        minHeight: {
          xs: 520,
          sm: 580,
          md: 650,
          lg: 620,
        },

        py: {
          xs: 8,
          sm: 10,
          md: 12,
          lg: 10,
        },

        overflow: 'hidden',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        color: 'white',
      }}
    >
      <Box
        component="img"
        src={psychiatryBackground}
        alt="Psychiatry care at Aspire Behavioral Health"
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'center',
          zIndex: -2,
        }}
      />

      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.45)',
          zIndex: -1,
        }}
      />

      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          px: 3,
          maxWidth: '850px',
        }}
      >
        <Typography
          variant="h2"
          sx={{
            fontWeight: 600,
            mb: 2,
            fontSize: { xs: '2.2rem', sm: '3.5rem' },
            lineHeight: 1.2,
          }}
        >
          Psychiatric Care That Feels Personal
        </Typography>

        <Typography
          variant="h6"
          sx={{
            fontWeight: 300,
            lineHeight: 1.8,
            mb: 4,
            fontSize: { xs: '1rem', sm: '1.2rem' },
          }}
        >
          Compassionate, evidence-based mental health care designed around your
          symptoms, goals, and long-term wellness.
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            href="tel:2108669850"
            sx={{
              backgroundColor: '#e3f3c5',
              color: 'black',
              fontWeight: 600,
              px: 4,
              py: 1.3,
              borderRadius: '30px',
              textTransform: 'none',
              '&:hover': { backgroundColor: '#d3eab0' },
            }}
          >
            Call 210-866-9850
          </Button>

          <Button
                        component="a"
                        href={zocdocLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="outlined"
                        sx={{
                          borderColor: 'white',
                          color: 'white !important',
                          fontWeight: 600,
                          px: 4,
                          py: 1.3,
                          borderRadius: '30px',
                          textTransform: 'none',
                          '&:visited': {
                            color: 'white',
                          },
                          '&:hover': {
                            borderColor: 'white',
                            backgroundColor: 'rgba(255,255,255,0.1)',
                            color: 'white',
                          },
                        }}
                      >
                        Schedule an Appointment
                      </Button>
        </Box>
      </Box>
    </Box>

    {/* Main Content */}
    <Box sx={{ backgroundColor: 'white', py: 8, px: 2 }}>
      {/* Approach Section */}
      <Box sx={{ textAlign: 'center', maxWidth: '900px', mx: 'auto', mb: 7 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 2 }}>
          Our Approach
        </Typography>

        <Typography
          variant="body1"
          sx={{
            lineHeight: 1.9,
            color: 'text.secondary',
            fontSize: { xs: '1rem', sm: '1.05rem' },
          }}
        >
          At Aspire Behavioral Health, we believe mental health care should be
          thoughtful, compassionate, and tailored to each person. Our providers
          take time to understand your symptoms, history, lifestyle, and goals
          before creating a treatment plan that fits your needs.
          <br />
          <br />
          We use evidence-based psychiatric care to support patients dealing
          with depression, anxiety, ADHD, mood disorders, sleep concerns,
          trauma-related symptoms, substance use concerns, and other mental
          health conditions.
          <br />
          <br />
          We accept most major insurance plans and strive to offer timely access
          to care. To schedule an appointment, call{' '}
          <a href="tel:2108669850">210-866-9850</a>.
        </Typography>
      </Box>

      <Divider
        sx={{
          width: { xs: '80%', sm: '60%', md: '40%' },
          mx: 'auto',
          mb: 7,
          borderColor: '#d9d9d9',
        }}
      />

      {/* Conditions Section */}
      <Box
        sx={{
          backgroundColor: '#e3f3c5',
          color: 'black',
          maxWidth: '1200px',
          mx: 'auto',
          p: { xs: 3, sm: 5 },
          borderRadius: 4,
          boxShadow: '0px 4px 12px rgba(0,0,0,0.12)',
          mb: 8,
        }}
      >
        <Typography
          variant="h4"
          sx={{ textAlign: 'center', fontWeight: 600, mb: 2 }}
        >
          Conditions We Treat
        </Typography>

        <Typography
          variant="body1"
          sx={{
            textAlign: 'center',
            maxWidth: '750px',
            mx: 'auto',
            mb: 4,
            lineHeight: 1.7,
          }}
        >
          We provide psychiatric care for a wide range of mental health concerns.
          If you are not sure what kind of support you need, our team can help
          guide you through the next step.
        </Typography>

        <Grid container spacing={2} justifyContent="center" alignItems="stretch">
          {conditions.map((condition) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              key={condition}
              sx={{ display: 'flex', justifyContent: 'center' }}
            >
              <Card
                sx={{
                  width: '100%',
                  maxWidth: 320,
                  borderRadius: 3,
                  boxShadow: 'none',
                  backgroundColor: 'rgba(255,255,255,0.75)',
                }}
              >
                <CardContent sx={{ textAlign: 'center', p: 2.5 }}>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {condition}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Services */}
      <Box sx={{ maxWidth: '1000px', mx: 'auto' }}>
        <Typography
          variant="h4"
          sx={{
            textAlign: 'center',
            fontWeight: 600,
            mb: 2,
          }}
        >
          Psychiatric Services
        </Typography>

        <Typography
          variant="body1"
          sx={{
            textAlign: 'center',
            maxWidth: '750px',
            mx: 'auto',
            mb: 5,
            lineHeight: 1.8,
            color: 'text.secondary',
          }}
        >
          Our psychiatry services are focused on helping you better understand
          your mental health, explore treatment options, and feel supported
          throughout your care.
        </Typography>

        <Card
          sx={{
            borderRadius: 4,
            boxShadow: '0px 4px 14px rgba(0,0,0,0.1)',
            mb: 4,
          }}
        >
          <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
              Medication Management
            </Typography>

            <Typography sx={{ lineHeight: 1.9, color: 'text.secondary' }}>
              Medication can be an important part of mental health treatment,
              but finding the right option should feel thoughtful, safe, and
              personalized. Our providers take time to understand your symptoms,
              medical history, concerns, and goals before recommending a
              treatment plan.
              <br />
              <br />
              We use medication management to help support conditions such as
              depression, anxiety, ADHD, bipolar disorder, insomnia, and other
              mental health concerns. Your provider will monitor your progress,
              make adjustments when needed, and work with you through shared
              decision-making.
              <br />
              <br />
              Our goal is to help you feel more balanced, more in control, and
              supported throughout your care.
            </Typography>
          </CardContent>
        </Card>

        <Card
          sx={{
            borderRadius: 4,
            boxShadow: '0px 4px 14px rgba(0,0,0,0.1)',
          }}
        >
          <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
              Personalized Treatment Planning
            </Typography>

            <Typography sx={{ lineHeight: 1.9, color: 'text.secondary' }}>
              Every patient responds differently to treatment, which is why we
              do not believe in one-size-fits-all care. Your provider will work
              with you to review your symptoms, discuss your options, and create
              a plan that supports both immediate concerns and long-term
              wellness.
              <br />
              <br />
              Treatment may include medication management, lifestyle
              recommendations, referrals for therapy when appropriate, or
              advanced treatment options such as TMS or SPRAVATO® depending on
              your needs.
            </Typography>
          </CardContent>
        </Card>

        {/* Bottom CTA */}
        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
            Ready to take the next step?
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              href="tel:2108669850"
              sx={{
                backgroundColor: '#e3f3c5',
                color: 'black',
                fontWeight: 600,
                px: 4,
                py: 1.3,
                borderRadius: '30px',
                textTransform: 'none',
                '&:hover': { backgroundColor: '#d3eab0' },
              }}
            >
              Call 210-866-9850
            </Button>

            <Button
                            component="a"
                            href={zocdocLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="outlined"
                            sx={{
                              backgroundColor: 'transparent',
                              border: '2px solid #3a4c17',
                              color: '#3a4c17 !important',
                              fontWeight: 600,
                              px: 4,
                              py: 1.3,
                              borderRadius: '30px',
                              textTransform: 'none',
                              '&:visited': {
                                color: '#3a4c17',
                              },
                              '&:hover': {
                                backgroundColor: 'rgba(58,76,23,0.05)',
                                border: '2px solid #3a4c17',
                                color: '#3a4c17',
                              },
                            }}
                          >
                            Schedule an Appointment
                          </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  </>
);

export default Psychiatry;